import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// ============ FIXED: Handle both with and without /api prefix ============
const getBaseURL = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const cleanUrl = url.replace(/\/$/, '');
  if (cleanUrl.endsWith('/api')) {
    return cleanUrl;
  }
  return `${cleanUrl}/api`;
};

const API_URL = getBaseURL();

// Get backend URL for file downloads (without /api)
const getFileBaseURL = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return url.replace(/\/$/, '').replace(/\/api$/, '');
};

const BACKEND_URL = getFileBaseURL();

const AdminKYC = () => {
  const [kycRequests, setKycRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchKYCRequests();
  }, []);

  const fetchKYCRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using API_URL with auto /api prefix
      const response = await axios.get(`${API_URL}/kyc/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKycRequests(response.data.kycRequests);
    } catch (error) {
      toast.error('Failed to load KYC requests');
    } finally {
      setLoading(false);
    }
  };

  const verifyKYC = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using API_URL with auto /api prefix
      await axios.put(`${API_URL}/kyc/admin/verify/${id}`, 
        { status, rejectionReason: status === 'rejected' ? rejectionReason : '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`KYC ${status} successfully`);
      setSelectedKYC(null);
      setRejectionReason('');
      fetchKYCRequests();
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">KYC Verification Requests</h1>
      
      <div className="overflow-x-auto">
        <table className="w-full bg-white rounded-lg shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">Investor</th>
              <th className="px-6 py-3 text-left">PAN</th>
              <th className="px-6 py-3 text-left">Aadhar</th>
              <th className="px-6 py-3 text-left">Submitted</th>
              <th className="px-6 py-3 text-left">Status</th>
              <th className="px-6 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {kycRequests.map((kyc) => (
              <tr key={kyc._id}>
                <td className="px-6 py-4">
                  <div className="font-medium">{kyc.user?.name}</div>
                  <div className="text-sm text-gray-500">{kyc.user?.email}</div>
                </td>
                <td className="px-6 py-4">{kyc.documents?.panCard?.number || '-'}</td>
                <td className="px-6 py-4">{kyc.documents?.aadharCard?.number || '-'}</td>
                <td className="px-6 py-4 text-sm">{new Date(kyc.submittedAt).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    kyc.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    kyc.status === 'approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {kyc.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => setSelectedKYC(kyc)}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Review Modal */}
      {selectedKYC && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Review KYC Documents</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="font-semibold">Investor:</p>
                <p>{selectedKYC.user?.name} ({selectedKYC.user?.email})</p>
              </div>
              <div>
                <p className="font-semibold">PAN Number:</p>
                <p>{selectedKYC.documents?.panCard?.number}</p>
                {selectedKYC.documents?.panCard?.url && (
                  // ✅ FIXED: Using BACKEND_URL instead of hardcoded localhost
                  <a href={`${BACKEND_URL}/${selectedKYC.documents.panCard.url}`} target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm">
                    View PAN Card
                  </a>
                )}
              </div>
              <div>
                <p className="font-semibold">Aadhar Number:</p>
                <p>{selectedKYC.documents?.aadharCard?.number}</p>
                {selectedKYC.documents?.aadharCard?.frontUrl && (
                  <div className="flex gap-2">
                    <a href={`${BACKEND_URL}/${selectedKYC.documents.aadharCard.frontUrl}`} target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm">
                      View Front
                    </a>
                    <a href={`${BACKEND_URL}/${selectedKYC.documents.aadharCard.backUrl}`} target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm">
                      View Back
                    </a>
                  </div>
                )}
              </div>
              {selectedKYC.documents?.passportPhoto?.url && (
                <div>
                  <p className="font-semibold">Photo:</p>
                  <a href={`${BACKEND_URL}/${selectedKYC.documents.passportPhoto.url}`} target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm">
                    View Photo
                  </a>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Rejection Reason (if rejecting)</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="2"
                  placeholder="Enter reason for rejection..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => verifyKYC(selectedKYC._id, 'approved')}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  onClick={() => verifyKYC(selectedKYC._id, 'rejected')}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => setSelectedKYC(null)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminKYC;